// The Operations workbench's shared reading of what is on screen: which thing
// the operator has selected, what the authoring sheet is opening, the route
// palette, and the point each drawn thing stands at.
//
// Every surface — the three rail panes, the map and the details sheet — names a
// selection through this module, so a click on a trap circle and a click on a
// trap row select the same thing, and the details sheet has one shape to switch
// on. The geometry here is a projection only: a route's line is its members'
// own targets, never a stored geometry (a Route carries none).

import type {
	MapReviewSiteRow,
	MapReviewTrapInstanceRow,
	MapReviewTrapStationRow,
	OperationsWorkRow,
	RoutePlan,
	SyncAssignment,
	SyncRoute,
	SyncRouteMember,
} from "@/type/api";
import type { GeoJSON } from "geojson";

/** What the Route tool carries in when it is opened from a selection: the Work
 * rows to compose into the Route, and/or the draft Assignments to start it with.
 * The seed is bumped per door press, so the form can tell one press from the
 * next — and a repeat press after widening the selection carries only what is
 * new. Zero is a pane opened from its own icon, which carries nothing. */
export interface RouteCarry {
	work: OperationsWorkRow[];
	assignmentIds: number[];
	/** The technician the carried assignments belong to, or zero when the door
	 * carried none. A Route binds exactly one technician (§8 Rule 2), so this is
	 * the field the form pre-fills when a selection implies it. */
	technicianId: number;
	seed: number;
}

/** The five things the workbench can have selected. A station is selected as
 * the ring drawn around its instances, not as one of them: the same Site can
 * carry either, and the operator needs both readings. */
export type SelectionKind =
	| "assignment"
	| "route"
	| "station"
	| "trap"
	| "technician"
	| "work_lead"
	| "work_order"
	| "work_direct";

/** One selection: the kind and the row id, which together name everything the
 * panes, the map and the details sheet need. This is the page's narrowed
 * projection of the shared `useWorkbenchSelection` record — the workbench store
 * is the single source, and a selection is this shape only because every row
 * Operations selects is addressed by a numeric id. It is never state of its
 * own. */
export interface Selection {
	kind: SelectionKind;
	id: number;
}

/** What the right rail's Assignment pane was opened to write. `collect`
 * carries the open trap instance the collect form was pre-filled from;
 * `assignment` may carry the technician "Assign work" started from; `set_trap`
 * carries the Site a selected station stands on, because a Site is what an
 * Assignment targets — the station only says where the work belongs. A Route is
 * not here: it is a sibling tool on the same rail with its own form. */
export type AuthoringState =
	| { kind: "assignment"; technicianId?: number }
	| { kind: "collect"; trap: MapReviewTrapInstanceRow }
	| { kind: "set_trap"; siteId: number };

/** What the map collected for the authoring form: a Site picked by its dot, the
 * parcel a pick landed on, or a point or area the operator drew. A parcel
 * carries both halves of its identity and the point that landed on it — an APN
 * is not unique across counties, and the landed point is where the work is
 * placed, which is also where a station would stand. */
export type MapPickTarget =
	| { kind: "site"; siteId: number }
	| {
			kind: "parcel";
			externalId: string;
			region: string;
			lng: number;
			lat: number;
	  }
	| { kind: "point"; geometry: GeoJSON.Point }
	| { kind: "area"; geometry: GeoJSON.Polygon };

/** What the map is collecting while the form is choosing its target: a pick (the
 * operator clicks the Site, station or parcel under the pointer), a point, or an
 * area. Null is the map reading the collection, which is what it has always
 * done. */
export type TargetDrawMode = "pick" | "point" | "area";

/**
 * The route palette. A fixed ramp keyed by route id: the same route keeps the
 * same colour across reloads and across the pane and the map, which is what
 * makes a line traceable back to its row. The members are the Bootstrap theme
 * hues the rest of the app already speaks in, interleaved so adjacent ids do
 * not read as near-identical lines.
 */
const ROUTE_PALETTE = [
	"#0d6efd",
	"#d63384",
	"#198754",
	"#fd7e14",
	"#6f42c1",
	"#20c997",
	"#dc3545",
	"#0dcaf0",
	"#6610f2",
	"#e83e8c",
];

/** The colour a route draws in, in the pane and on the map alike. */
export function routeColor(routeId: number): string {
	const index = Math.abs(routeId) % ROUTE_PALETTE.length;
	return ROUTE_PALETTE[index];
}

/** The map's two trap readings: orange while a trap is out (the collection it
 * is waiting for), grey once it has been collected — "no trap currently
 * deployed at that instance" reads as grey rather than as a second category. */
export const TRAP_OPEN_COLOR = "#fd7e14";
export const TRAP_COLLECTED_COLOR = "#6c757d";

/** The representative point a Site ledger row carries, or null when the Site
 * has none on file. */
export function sitePoint(site: MapReviewSiteRow): [number, number] | null {
	if (site.latitude === null || site.longitude === null) return null;
	return [site.longitude, site.latitude];
}

/** The point a Trap Station stands at: the station's own recorded location,
 * which its QUERY row carries so the ring can be drawn without reading each
 * station's detail. A station whose location has never been recorded has no
 * ring to draw. */
export function stationPoint(
	station: MapReviewTrapStationRow,
): [number, number] | null {
	if (station.latitude === null || station.longitude === null) return null;
	return [station.longitude, station.latitude];
}

/** A drawn target's point: a Point as itself, anything else as the centre of
 * its bounding box — the same representative reading the ledger rows use. */
export function geometryPoint(
	geometry: GeoJSON.Geometry | null,
): [number, number] | null {
	if (geometry === null) return null;
	const coordinates: [number, number][] = [];
	collectCoordinates(geometry, coordinates);
	if (coordinates.length === 0) return null;
	if (geometry.type === "Point") return coordinates[0];
	let minLng = coordinates[0][0];
	let maxLng = minLng;
	let minLat = coordinates[0][1];
	let maxLat = minLat;
	for (const [lng, lat] of coordinates) {
		if (lng < minLng) minLng = lng;
		if (lng > maxLng) maxLng = lng;
		if (lat < minLat) minLat = lat;
		if (lat > maxLat) maxLat = lat;
	}
	return [(minLng + maxLng) / 2, (minLat + maxLat) / 2];
}

function collectCoordinates(value: unknown, out: [number, number][]): void {
	if (Array.isArray(value)) {
		if (
			value.length >= 2 &&
			typeof value[0] === "number" &&
			typeof value[1] === "number"
		) {
			out.push([value[0], value[1]]);
			return;
		}
		for (const item of value) collectCoordinates(item, out);
		return;
	}
	if (value !== null && typeof value === "object") {
		const record = value as { coordinates?: unknown; geometry?: unknown };
		if (record.coordinates !== undefined) {
			collectCoordinates(record.coordinates, out);
			return;
		}
		if (record.geometry !== undefined) collectCoordinates(record.geometry, out);
	}
}

/** The point an Assignment is drawn at: its Site's representative point when
 * the target is a Site, else its drawn geometry's point. A Feature, Facilitator
 * or target-less row has no point to draw. */
export function assignmentPoint(
	assignment: SyncAssignment,
	siteIndex: Map<number, MapReviewSiteRow>,
): [number, number] | null {
	if (assignment.targetSiteId !== null) {
		const site = siteIndex.get(assignment.targetSiteId);
		return site ? sitePoint(site) : null;
	}
	return geometryPoint(assignment.targetGeometry);
}

/** The point a picked target is drawn at: a Site through the index, read the
 * same way an Assignment's Site target is; a parcel at the point the pick
 * landed on, because the parcel's own outline is the map's to draw and the
 * landed point is where the work goes; a drawn point or area at its own
 * representative point. A Site the index does not carry has no point. */
export function pickTargetPoint(
	target: MapPickTarget,
	siteIndex: Map<number, MapReviewSiteRow>,
): [number, number] | null {
	if (target.kind === "site") {
		const site = siteIndex.get(target.siteId);
		return site ? sitePoint(site) : null;
	}
	if (target.kind === "parcel") return [target.lng, target.lat];
	return geometryPoint(target.geometry);
}

/** A Route's positioned members, in sequence order. */
export function orderedMembers(route: SyncRoute): SyncRouteMember[] {
	return route.members
		.filter((member) => member.position !== null)
		.sort((left, right) => (left.position ?? 0) - (right.position ?? 0));
}

/** A Route's members that hold no position yet (decision 10). */
export function unsequencedMembers(route: SyncRoute): SyncRouteMember[] {
	return route.members.filter((member) => member.position === null);
}

/** One member of a Route that has a place on the map: its assignment and the
 * point its target stands at. */
export interface RouteStop {
	assignmentId: number;
	position: number | null;
	point: [number, number];
}

/** Every member of the Route that can be drawn, in sequence order with the
 * unsequenced members after — the order the details sheet lists them in. */
export function routeStops(
	route: SyncRoute,
	assignmentIndex: Map<number, SyncAssignment>,
	siteIndex: Map<number, MapReviewSiteRow>,
): RouteStop[] {
	const positioned = orderedMembers(route);
	const stops: RouteStop[] = [];
	for (const member of [...positioned, ...unsequencedMembers(route)]) {
		const assignment = assignmentIndex.get(member.assignmentId);
		if (!assignment) continue;
		const point = assignmentPoint(assignment, siteIndex);
		if (point === null) continue;
		stops.push({
			assignmentId: member.assignmentId,
			position: member.position,
			point,
		});
	}
	return stops;
}

/** The LineString through a Route's sequenced members, in position order. A
 * member whose target has no point contributes no vertex; unsequenced members
 * are not part of the line at all. */
export function routeLine(
	route: SyncRoute,
	assignmentIndex: Map<number, SyncAssignment>,
	siteIndex: Map<number, MapReviewSiteRow>,
): [number, number][] {
	const positioned = new Set(orderedMembers(route).map((m) => m.assignmentId));
	return routeStops(route, assignmentIndex, siteIndex)
		.filter(
			(stop) => stop.position !== null && positioned.has(stop.assignmentId),
		)
		.map((stop) => stop.point);
}

/** The road the plan drives, as one run of [lng, lat] vertices: the plan's
 * stored MultiLineString flattened in leg order. A leg ends where the next
 * begins, so the legs are one line and the map draws the Route once rather than
 * once per leg. Only an `ok` or `partial` plan carries a line — a failure (and
 * an engine that could not be reached) clears the path rather than keeping a
 * stale one — and a plan that routed nothing has no line to draw: both answer
 * null, and the straight line through the members' own targets is what the map
 * falls back to. */
export function planLine(plan: RoutePlan): [number, number][] | null {
	if (plan.status !== "ok" && plan.status !== "partial") return null;
	if (plan.path === null) return null;
	const line: [number, number][] = [];
	collectCoordinates(plan.path.coordinates, line);
	return line.length >= 2 ? line : null;
}

/** The members a route draws as markers rather than as line vertices: the
 * unsequenced ones, plus every drawable member of a route too short to make a
 * line (a one-stop route would otherwise have no place on the map). */
export function routeMarkers(
	route: SyncRoute,
	assignmentIndex: Map<number, SyncAssignment>,
	siteIndex: Map<number, MapReviewSiteRow>,
): RouteStop[] {
	const stops = routeStops(route, assignmentIndex, siteIndex);
	const line = routeLine(route, assignmentIndex, siteIndex);
	if (line.length >= 2) {
		return stops.filter((stop) => stop.position === null);
	}
	return stops;
}
